import { API } from "@swivl-mono/api-helper";
import type { UserStorySimilar } from "@swivl-mono/api-helper";
import { filterClasses } from "@swivl-mono/helpers";
import { type BotPromptChannel, Domain, STORAGE_CATEGORIES, type UserStory, type UserStoryEvalSpec, type UserStoryStatus } from "@swivl-mono/types";
import { Button, ButtonColor, ButtonVariant, Input, Modal } from "@swivl-mono/ui";
import { useState } from "react";

interface UserStoryAddModalProps {
  defaultDomain?: Domain;
  onClose: () => void;
  onAdded: (story: UserStory) => void;
}

/**
 * Sentinel id passed to the server when adding a new story. The server's
 * `AdminUserStoryService.upsert` recognizes this and assigns the next
 * available `us-NNNN` id before saving. Studio doesn't ask the user to
 * pick an id manually.
 */
const NEW_STORY_ID_SENTINEL = "__new__";

type AddMode = "manual" | "ai";

export function UserStoryAddModal({ defaultDomain, onClose, onAdded }: UserStoryAddModalProps): JSX.Element {
  const [mode, setMode] = useState<AddMode>("manual");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<string>(STORAGE_CATEGORIES[0]);
  const [status, setStatus] = useState<UserStoryStatus>("backlog");
  const [channels, setChannels] = useState<BotPromptChannel[]>(["web", "sms", "voice"]);
  const [domain, setDomain] = useState<Domain>(defaultDomain ?? Domain.Storage);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // AI-draft mode state
  const [aiPrompt, setAiPrompt] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [aiNotes, setAiNotes] = useState<string | undefined>(undefined);
  const [aiEval, setAiEval] = useState<UserStoryEvalSpec | undefined>(undefined);
  const [similar, setSimilar] = useState<UserStorySimilar[]>([]);
  const [hasDrafted, setHasDrafted] = useState(false);

  const handleDraft = async (): Promise<void> => {
    setError(null);
    if (!aiPrompt.trim()) {
      setError("Describe the story first.");
      return;
    }
    setDrafting(true);
    try {
      const result = await API.UserStories.draft(aiPrompt.trim(), domain);
      const d = result.draft;
      // Load fields from the drafted partial — user reviews + edits before saving.
      if (d.title) setTitle(d.title);
      if (d.description) setDescription(d.description);
      if (d.category) setCategory(d.category);
      if (d.notes !== undefined) setAiNotes(d.notes);
      if (d.eval) setAiEval(d.eval);
      if (d.channelStatus && Object.keys(d.channelStatus).length > 0) {
        const ch = Object.keys(d.channelStatus) as BotPromptChannel[];
        setChannels(ch);
        const first = Object.values(d.channelStatus)[0];
        if (first) setStatus(first);
      }
      setSimilar(result.similarStories);
      setHasDrafted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Drafting failed");
    } finally {
      setDrafting(false);
    }
  };

  const handleSubmit = async (): Promise<void> => {
    setError(null);
    if (channels.length === 0) {
      setError("Pick at least one channel.");
      return;
    }

    const channelStatus: Partial<Record<BotPromptChannel, UserStoryStatus>> = {};
    for (const c of channels) channelStatus[c] = status;

    const story: UserStory = {
      id: NEW_STORY_ID_SENTINEL, // server stamps the actual id on first save
      domain,
      category: category.trim(),
      title: title.trim(),
      description: description.trim(),
      channelStatus,
      ...(aiNotes ? { notes: aiNotes } : {}),
      ...(aiEval ? { eval: aiEval } : {}),
      // createdBy / createdAt are auto-stamped by the server on first save.
      updatedAt: new Date().toISOString()
    };

    setSaving(true);
    try {
      const result = await API.UserStories.upsert(story);
      onAdded(result.story);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleChannel = (c: BotPromptChannel): void => {
    setChannels((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Add User Story">
      <div className="flex flex-col gap-3 p-4">
        <div className="flex items-center gap-1 rounded border border-grey-200 p-1">
          <button
            type="button"
            onClick={() => setMode("manual")}
            className={filterClasses(
              "flex-1 rounded px-3 py-1.5 text-xs font-medium",
              mode === "manual" ? "bg-orange-500 text-white" : "text-grey-700 hover:bg-grey-100"
            )}
          >
            Manual
          </button>
          <button
            type="button"
            onClick={() => setMode("ai")}
            className={filterClasses(
              "flex-1 rounded px-3 py-1.5 text-xs font-medium",
              mode === "ai" ? "bg-orange-500 text-white" : "text-grey-700 hover:bg-grey-100"
            )}
          >
            ✨ Draft with AI
          </button>
        </div>

        {mode === "ai" && (
          <div className="border-primary-200 bg-primary-50 rounded border p-3">
            <label className="mb-1 block text-xs font-medium text-grey-600">Describe the story</label>
            <textarea
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              rows={3}
              className="w-full rounded border border-grey-200 bg-white px-2 py-1 text-sm"
              placeholder="e.g. user can transfer their lease to a new tenant who comes in to the office"
            />
            <div className="mt-2 flex items-center gap-2">
              <Button color={ButtonColor.BLUE} onClick={handleDraft} disabled={drafting || !aiPrompt.trim()}>
                {drafting ? "Drafting…" : "Draft"}
              </Button>
              {hasDrafted && <span className="text-xs text-grey-500">Draft loaded — review the fields below before saving.</span>}
            </div>
          </div>
        )}

        {similar.length > 0 && (
          <div className="rounded border border-yellow-300 bg-yellow-50 px-3 py-2">
            <div className="mb-1 text-xs font-semibold text-yellow-800">
              ⚠ This looks similar to {similar.length} existing {similar.length === 1 ? "story" : "stories"}:
            </div>
            <ul className="space-y-0.5 text-xs text-yellow-900">
              {similar.map((s) => (
                <li key={s.id}>
                  <span className="font-mono">{s.id}</span> — <strong>{s.title}</strong>: {s.why}
                </li>
              ))}
            </ul>
            <div className="mt-1 text-[11px] text-yellow-700">If one of these covers what you're adding, jump to it instead. Otherwise proceed.</div>
          </div>
        )}

        <p className="text-xs text-grey-500">A new id (`us-NNNN`) will be assigned on save.</p>
        <Input label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
        <div>
          <label className="mb-1 block text-xs font-medium text-grey-600">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="w-full rounded border border-grey-200 px-2 py-1 text-sm"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-grey-600">Category</label>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full rounded border border-grey-200 px-2 py-1 text-sm">
            {STORAGE_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-grey-600">Domain</label>
            <select
              value={domain}
              onChange={(e) => setDomain(e.target.value as Domain)}
              className="w-full rounded border border-grey-200 px-2 py-1 text-sm"
            >
              {Object.values(Domain).map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-grey-600">Status (all channels)</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as UserStoryStatus)}
              className="w-full rounded border border-grey-200 px-2 py-1 text-sm"
            >
              {(["backlog", "building", "live", "deprecated"] as const).map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-grey-600">Channels</label>
            <div className="flex gap-1">
              {(["web", "sms", "voice"] as const).map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => toggleChannel(c)}
                  className={`flex-1 rounded border px-1 py-1 text-xs ${channels.includes(c) ? "border-primary-500 bg-primary-50 text-primary-700" : "border-grey-200 text-grey-700"}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>

        {error && <div className="rounded border border-red-300 bg-red-50 px-2 py-1 text-xs text-red-700">{error}</div>}

        <div className="flex justify-end gap-2">
          <Button variant={ButtonVariant.GHOST} onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button color={ButtonColor.BLUE} onClick={handleSubmit} disabled={saving || !title || !description || !category || channels.length === 0}>
            {saving ? "Adding…" : "Add Story"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
